import { Component, Injectable } from '@angular/core';
import {
  MatLegacyDialog as MatDialog,
  MatLegacyDialogConfig as MatDialogConfig,
} from '@angular/material/legacy-dialog';

import { Observable } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';

import { ConfirmDialogComponent } from '@shared/components/confirm-dialog/confirm-dialog.component';
import { IConfirmDialogConfig } from '@models/dialog';
import { GachaDialogComponent } from '@app/infrastructure/shared/components/gacha-dialog/gacha-dialog.component';
import { Critter } from '@app/infrastructure/models/critter';
import { CritterDialogComponent } from '@app/infrastructure/shared/components/critter-dialog/critter-dialog.component';
import { AvatarSelectionDialogComponent } from '@app/infrastructure/shared/components/avatar-selection-dialog/avatar-selection-dialog.component';
import { AssignRetryDialogComponent } from '@shared/components/assign-retry-dialog/assign-retry-dialog.component';
import { PageTurnerDialogComponent } from '@shared/components/page-turner-dialog/page-turner-dialog.component';
import { FileUpload } from '@models/file';
import {
  CopyLinkDialog,
  ILinkDialog,
} from '@shared/components/copy-link-dialog/copy-link-dialog.component';
import { StudentInvitesDialogComponent } from '@shared/components/student-invites-dialog/student-invites-dialog.component';
import { LanguageDialogComponent } from '@shared/components/language-dialog/language-dialog.component';
import { ITeacherClassDetailDTO } from '@models/teacher-classroom';
import { component } from '@shared/grade-unit/grade-unit-routing.module';
import { ComponentType } from '@angular/cdk/overlay';

@Injectable({
  providedIn: 'root',
})
export class ModalService {
  constructor(private dialog: MatDialog) {}

  public openConfirmDialog(
    dialogConfig: IConfirmDialogConfig,
  ): Observable<any> {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: dialogConfig,
      autoFocus: false,
    });
    let confirmDialog;

    if (dialogConfig.showOpenField) {
      confirmDialog = dialogRef.afterClosed().pipe(take(1));
    } else {
      confirmDialog = dialogRef.beforeClosed().pipe(take(1));
    }

    return confirmDialog;
  }

  public openCopyLinkDialog(data: ILinkDialog): Observable<boolean> {
    const dialogRef = this.dialog.open(CopyLinkDialog, {
      hasBackdrop: true,
      data,
    });
    return dialogRef.beforeClosed().pipe(take(1));
  }

  public openAvatarDialog(): Observable<boolean> {
    const dialogRef = this.dialog.open(AvatarSelectionDialogComponent, {
      hasBackdrop: true,
      panelClass: 'avatar-dialog-container',
      width: '600px',
      height: '600px',
    });

    return dialogRef.beforeClosed().pipe(take(1));
  }

  public openGachaDialog(): Observable<boolean> {
    const dialogRef = this.dialog.open(GachaDialogComponent, {
      hasBackdrop: true,
      panelClass: 'gacha-dialog-container',
      width: '600px',
      height: '600px',
    });
    let confirmDialog;

    confirmDialog = dialogRef.beforeClosed().pipe(take(1));

    return confirmDialog;
  }

  public openCritterDialog(critter: Critter): Observable<boolean> {
    const dialogRef = this.dialog.open(CritterDialogComponent, {
      hasBackdrop: true,
      panelClass: 'critter-dialog-container',
      data: critter,
    });

    return dialogRef.beforeClosed().pipe(take(1));
  }

  public openAssignRetryDialog(studentName: string): Observable<string> {
    const dialogRef = this.dialog.open(AssignRetryDialogComponent, {
      hasBackdrop: true,
      panelClass: 'assign-retry-dialog',
      data: studentName,
    });

    return dialogRef.afterClosed().pipe(take(1));
  }

  public openPagerDialog(file: FileUpload): Observable<string> {
    const dialogRef = this.dialog.open(PageTurnerDialogComponent, {
      hasBackdrop: true,
      panelClass: 'pager-dialog-container',
      data: file,
    });
    return dialogRef.afterClosed().pipe(take(1));
  }

  public openInviteStudentsDialog(
    classroom: ITeacherClassDetailDTO = null,
  ): Observable<boolean> {
    const dialogRef = this.dialog.open(StudentInvitesDialogComponent, {
      hasBackdrop: true,
      data: classroom,
    });
    return dialogRef.afterClosed().pipe(take(1));
  }

  public openLanguageDialog(): Observable<boolean> {
    const dialogRef = this.dialog.open(LanguageDialogComponent, {
      hasBackdrop: true,
      panelClass: 'language-dialog-container',
    });
    return dialogRef.afterClosed().pipe(take(1));
  }

  public openDialog(
    dialogComponent: ComponentType<unknown>,
    dialogConfig: MatDialogConfig,
  ) {
    const dialogRef = this.dialog.open(dialogComponent, dialogConfig);
    return dialogRef.afterClosed().pipe(take(1));
  }
}
